// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Divider, Form, message, Modal, Popconfirm, Spin } from "antd";
import { LoadingOutlined, TeamOutlined } from "@ant-design/icons";
import {
  UserInfoResponseDTO,
  UserUpdateRequestDTO
} from "../../../../api/backend-openapi";
import UserSettingsFormSection from "./UserSettingsFormSection";
import { formItemBlockLayout } from "../../../../utils/Constant";
import { navigate } from "@reach/router";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  QuestionCircleOutlined,
  SaveOutlined,
  StopOutlined
} from "@ant-design/icons/lib";
import DateFormat from "../../../../utils/DateFormat";
import i18n from "i18next";
import { showConfirm } from "../../../components/ConfirmModal";
import { requestGetNewsThemes } from "../../../../state/newsTheme/NewsThemeEffects";
import {
  requestDeleteUser,
  requestGetUser,
  requestUpdateUser,
  requestStatusUser
} from "../../../../state/userManagement/UserManagementEffects";
import {
  deleteUser,
  updateUser
} from "../../../../state/userManagement/UserManagementEvents";
import UserStatusTag from "./UserStatusTag";
import {
  Field,
  UserEditFormProps,
  UserWorkflowStatus
} from "../../../types/UserManagementTypes";

const { confirm } = Modal;

export interface UserWorkflowStatusLayout {
  label: string;
  color: string;
}

const UserForm: React.FunctionComponent<UserEditFormProps> = (
  props: UserEditFormProps
) => {
  const {
    userId,
    editMode,
    setEditMode,
    canEdit,
    canDelete,
    canValidate
  } = props;

  const { t } = useTranslation();

  const [form] = Form.useForm();

  const [userDetails, setUserDetails] = useState<UserInfoResponseDTO | null>(
    null
  );

  const [getResponseReceived, setGetResponseReceived] = useState<boolean>(
    false
  );

  const [fields, setFields] = useState<Field[]>([]);

  const [branch, setBranch] = useState<string | undefined>(undefined);

  useEffect(() => {
    setBranch(userDetails?.branch);
  }, [userDetails]);

  const [buttonLoading, setButtonLoading] = useState<boolean>(false);

  const resetFormFields = (data: UserInfoResponseDTO) => {
    setFields([
      {
        name: ["id"],
        value: data.id
      },
      {
        name: ["creationDate"],
        value: DateFormat.standardDate(data.creationDate)
      },
      {
        name: ["modificationDate"],
        value: DateFormat.standardDate(data.modificationDate)
      },
      {
        name: ["email"],
        value: data.email
      },
      {
        name: ["sub"],
        value: data.sub
      },
      {
        name: ["status"],
        value: data.status.toString()
      },
      {
        name: ["lastConnectionDate"],
        value: data.lastConnectionDate
          ? DateFormat.standardDate(data.lastConnectionDate)
          : ""
      },
      {
        name: ["creator"],
        value: data.creatorId
      },
      {
        name: ["profiles"],
        value: data.profiles
      },
      {
        name: ["branch"],
        value: data.branch || ""
      },
      ...(data.newsThemeIds
        ? [
            {
              name: ["newsThemeIds"],
              value: data.newsThemeIds || []
            }
          ]
        : [])
    ]);
    setBranch(data?.branch);
  };

  useEffect(() => {
    if (userId) {
      requestGetNewsThemes();
    }
  }, [userId]);

  useEffect(() => {
    return requestGetNewsThemes.done.watch(({ result }) => {
      if (result.ok && result.data) {
        requestGetUser(userId);
        setGetResponseReceived(true);
      } else {
        message.error(result.errorMessage);
      }
    });
  });

  useEffect(() => {
    return requestGetUser.done.watch(({ result }) => {
      setGetResponseReceived(true);
      if (result.ok) {
        if (result.data) {
          setUserDetails(result.data);
          resetFormFields(result.data);
        } else {
          message.error(result.errorMessage);
        }
      } else {
        message.error(result.errorMessage);
      }
    });
  });

  useEffect(() => {
    return requestUpdateUser.done.watch(({ result }) => {
      setButtonLoading(false);
      if (result.ok && result.data) {
        setEditMode(false);
        updateUser(result.data);
        setUserDetails(result.data);
        resetFormFields(result.data);
        message.success(t("edit_user.success"));
      } else {
        message.error(result.errorMessage);
      }
    });
  });

  useEffect(() => {
    return requestStatusUser.done.watch(({ result }) => {
      setButtonLoading(false);
      if (result.ok && result.data) {
        setEditMode(false);
        updateUser(result.data);
        setUserDetails(result.data);
        resetFormFields(result.data);
        result.data.status === UserWorkflowStatus.ACTIVE
          ? message.success(t("edit_user.active_success"))
          : message.success(t("edit_user.inactive_success"));
      } else {
        message.error(result.errorMessage);
      }
    });
  });

  useEffect(() => {
    return requestDeleteUser.done.watch(({ params, result }) => {
      if (result.ok) {
        deleteUser(params.id);
        message.success(t("user.global_buttons.delete.success"));
        navigate("/users");
      } else {
        message.error(t("user.global_buttons.delete.error"));
      }
    });
  });

  const spinIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  const confirmationPopup = async () => {
    return new Promise<boolean>(resolve => {
      confirm({
        title: t("edit_user.modal.title"),
        content: t("edit_user.modal.description"),
        okText: t("edit_user.modal.yes"),
        cancelText: t("edit_user.modal.no"),
        centered: true,
        onOk() {
          resolve(true);
        },
        onCancel() {
          resolve(false);
        }
      });
    });
  };

  const setValuesAndRequestUpdateUser = (
    values: any,
    status: UserWorkflowStatus
  ) => {
    if (userDetails) {
      const userToUpate: UserUpdateRequestDTO = {
        sub: values.sub,
        profiles: values.profiles,
        branch: values.branch,
        newsThemeIds: values.newsThemeIds,
        creator: values.creator,
        status
      };
      requestUpdateUser({
        id: userId,
        dto: userToUpate
      });
    }
  };

  const handleSubmit = (values: any, status: UserWorkflowStatus) => {
    confirmationPopup().then((confirmed: boolean) => {
      if (confirmed) {
        setValuesAndRequestUpdateUser(values, status);

        setButtonLoading(true);
      }
    });
  };

  const handleSave = (values: any) => {
    handleSubmit(values, UserWorkflowStatus.ACTIVE);
  };

  const onFinishFailed = ({ errorFields }: any) => {
    message.error(t("global.on_finish_failed"));
    form.scrollToField(errorFields[0].name);
  };

  return (
    <>
      {!getResponseReceived ? (
        <div style={{ textAlign: "center" }}>
          <Spin indicator={spinIcon} />
        </div>
      ) : userDetails ? (
        <Form
          form={form}
          {...formItemBlockLayout}
          onFinish={handleSave}
          onFinishFailed={onFinishFailed}
          fields={fields}
        >
          <div className="form-first-item-sticky-top-container">
            <div className="form-first-item-sticky-top-content">
              <div className="my-3">
                <UserStatusTag status={userDetails.status} />
              </div>
              <Divider orientation="left">
                <h3 className="text-secondary mb-0">
                  {t("user.user_actions")}
                </h3>
              </Divider>
              <div className="d-flex align-items-center justify-content-between flex-wrap px-3">
                <div className="my-3">
                  <div className="d-flex align-items-center justify-content-center flex-wrap">
                    {!editMode && (
                      <Button
                        htmlType="reset"
                        className="m-2"
                        onClick={() => navigate("/users")}
                      >
                        <ArrowLeftOutlined /> {t("edit_user.return")}
                      </Button>
                    )}
                  </div>
                </div>
                <div>
                  {editMode ? (
                    <div className="text-center m-2">
                      <Popconfirm
                        title={t("global.cancel_confirmation")}
                        okText={t("global.yes")}
                        cancelText={t("global.no")}
                        onConfirm={() => {
                          setEditMode(false);
                          resetFormFields(userDetails);
                        }}
                        placement="top"
                        icon={<QuestionCircleOutlined />}
                        className="m-2"
                      >
                        <Button htmlType="reset">
                          <StopOutlined /> {t("edit_user.cancel")}
                        </Button>
                      </Popconfirm>
                      <Button
                        className="btn-primary m-2"
                        htmlType="submit"
                        loading={buttonLoading}
                      >
                        <SaveOutlined /> {t("edit_user.save")}
                      </Button>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center justify-content-center flex-wrap">
                      {canEdit && (
                        <Button
                          htmlType="reset"
                          className="btn-primary m-2"
                          onClick={() => setEditMode(true)}
                        >
                          <EditOutlined /> {t("edit_user.button")}
                        </Button>
                      )}
                      {canValidate && (
                        <Button
                          className={
                            userDetails.status === UserWorkflowStatus.ACTIVE
                              ? "btn-secondary m-2"
                              : "btn-success m-2"
                          }
                          onClick={() =>
                            showConfirm(
                              i18n.t(
                                userDetails.status === UserWorkflowStatus.ACTIVE
                                  ? "user_list.table.confirmation.inactive"
                                  : "user_list.table.confirmation.active",
                                {
                                  user: userDetails.email
                                }
                              ),
                              () => {
                                requestStatusUser({
                                  id: userDetails.id,
                                  dto: {
                                    status:
                                      userDetails?.status ===
                                      UserWorkflowStatus.INACTIVE
                                        ? UserWorkflowStatus.ACTIVE
                                        : UserWorkflowStatus.INACTIVE
                                  }
                                });
                              }
                            )
                          }
                        >
                          {userDetails.status === UserWorkflowStatus.ACTIVE ? (
                            <>
                              <StopOutlined /> {t("edit_user.inactive")}
                            </>
                          ) : (
                            <>
                              <TeamOutlined /> {t("edit_user.active")}
                            </>
                          )}
                        </Button>
                      )}

                      {canDelete && (
                        <Button
                          className="btn-danger m-2"
                          onClick={() =>
                            showConfirm(
                              i18n.t("user_list.table.confirmation.delete", {
                                user: userDetails.email
                              }),
                              () => {
                                requestDeleteUser(userDetails);
                              }
                            )
                          }
                        >
                          <DeleteOutlined /> {t("edit_user.delete")}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <Divider orientation="left">
            <h3 className="text-secondary mb-0">{t("user.settings")}</h3>
          </Divider>
          <UserSettingsFormSection
            user={userDetails}
            editMode={editMode}
            branch={branch}
            setBranch={setBranch}
            resetNewsTheme={() =>
              form.setFields([
                {
                  name: "newsThemeIds",
                  value: []
                }
              ])
            }
            showRequired={editMode}
          />
        </Form>
      ) : (
        <div>{t("user.not_found")}</div>
      )}
    </>
  );
};

export default UserForm;
